'use client'

import { getCookie } from 'cookies-next'
import React, { useEffect, useState } from 'react'
import ForPartnersSelect from './ForPartnersSelect'
import { FieldValues, useForm } from 'react-hook-form'
import ForPartnersAutocomplete from './ForPartnersAutocomplete'
import ForPartnersMessage from './ForPartnersMessage'
import {
    selectTheme,
    selectDirection,
    selectOrganization,
    selectAmountOfHeads
} from '@/assets/data/review-data'
import ForPartnersNameInput from './ForPartnersNameInput'
import ForPartnersMail from './ForPartnersMail'
import ForPartnersPhone from './ForPartnersPhone'
import ForPartnersAgree from './ForPartnersAgree'
import ForPartnersOrganization from './ForPartnersOrganization'
import { useGetRegionsQuery, useSendFormDataMutation } from '@/redux/services/reviewApi'
import { useTranslation } from 'react-i18next'

const ForPartners = ({ title }) => {
    const { t } = useTranslation()
    const [labelsList] = useState({
        topic: t('footerFormthemeUp'),
        direction: t('footerFormProducts'),
        organizationType: t('footerFormImagine'),
        amountOfHeads: t('footerFormCountOfHeads')
    })
    const csrf = getCookie('csrf') || ""
    const [regions, setRegions] = useState([])
    const [phoneValue, setPhoneValue] = useState('')
    const [messageValue, setMessageValue] = useState('')
    const [submitForm] = useSendFormDataMutation()
    const [isSuccess, setIsSuccess] = useState(false)
    const [statesOfSelects, setStatesOfSelects] = useState({
        region: {
            isOpen: false,
            selected: ''
        },
        theme: {
            isOpen: false,
            selected: t('footerFormthemePlaceholder')
        },
        direction: {
            isOpen: false,
            selected: t('footerFormselect')
        },
        organizationType: {
            isOpen: false,
            selected: t('footerFormType')
        },
        amountOfHeads: {
            isOpen: false,
            selected: t('footerFormCount')
        }
    })

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
        reset,
        watch
    } = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit'
    })

    const { data: regionsData } = useGetRegionsQuery()

    useEffect(() => {
        setRegions(regionsData)
    }, [regionsData])

    const watchAllFields = watch()
    const fieldsFilled =
        watchAllFields.name &&
        watchAllFields.email &&
        watchAllFields.theme &&
        watchAllFields.message &&
        watchAllFields.robot

    const updateStateOfSelect = (key: string, value: boolean) => {
        setStatesOfSelects(prevState => ({
            ...prevState,
            [key]: {
                isOpen: value,
                selected: prevState[key]?.selected
            }
        }))
    }

    const toggleStateOfSelect = (key: string) => {
        setStatesOfSelects(prevState => ({
            ...prevState,
            [key]: {
                isOpen: !prevState[key]?.isOpen,
                selected: prevState[key]?.selected
            }
        }))
    }

    const setOption = (key, value) => {
        setStatesOfSelects(prevState => ({
            ...prevState,
            [key]: {
                isOpen: prevState[key]?.isOpen,
                selected: value
            }
        }))
    }

    const onSubmit = (data: FieldValues) => {
        const processedData = Object.fromEntries(
            Object.entries(data)?.map(([key, value]) => [key, value ?? ""])
        );

        processedData.token = csrf || ""
        processedData.robot = !processedData.robot
        processedData.heads_amount = processedData["heads-amount"] //FIX
        processedData.organization_type = processedData["organization-type"] //FIX
        processedData.message = messageValue
        delete (processedData["heads-amount"]) //FIX
        delete (processedData["organization-type"]) //FIX

        submitForm(processedData)

        setIsSuccess(true)
        setPhoneValue('')
        setMessageValue('')
        setValue('robot', false)
        setOption('region', '')
        setOption('theme', selectTheme[0].name)
        setOption('direction', selectDirection[0].name)
        setOption('organizationType', selectOrganization[0].name)
        setOption('amountOfHeads', selectAmountOfHeads[0].name)
        reset()
    }

    return (
        <div className='for-partners'>
            {title && <h2 className='main__section-title'>{t('partners')}</h2>}
            <div className='for-partners__top'>
                <div className='for-partners__text'>
                    {t("FeedbackText")}
                </div>
                <div className="footer__media-social">
                    <a target='_blank' href='https://vk.com/megamix_official' className="footer__media-social-link">
                        <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23.8498 33.2055C24.9534 33.2055 25.4046 32.5033 25.3919 31.6228C25.3357 28.307 26.6857 26.5219 29.1228 28.8484C31.8191 31.4222 32.3772 33.2055 35.6515 33.2055H41.45C42.9141 33.2055 43.4903 32.7558 43.4903 32.0501C43.4903 30.5573 40.9155 27.923 38.7338 25.9892C35.6787 23.2822 35.5356 23.2182 38.1666 19.9594C41.4301 15.9136 45.6992 10.7297 41.923 10.7297H34.7093C33.3104 10.7297 33.2089 11.4821 32.7106 12.603C30.9077 16.6626 27.4811 21.9209 26.1801 21.1166C24.8193 20.2777 25.4426 16.9549 25.5459 12.0166C25.5731 10.7124 25.5658 9.81815 23.4784 9.35459C22.3386 9.10378 21.2297 9 20.2004 9C16.0817 9 13.2404 10.6484 14.855 10.9355C17.7016 11.4423 17.428 17.3216 16.7648 19.8608C15.6088 24.282 11.2635 16.3599 9.45332 12.4144C9.01662 11.4666 8.88253 10.7297 7.32419 10.7297H1.42606C0.534547 10.7297 0 11.0065 0 11.6222C0 12.6635 5.36359 23.2459 10.4844 28.5215C15.4783 33.6674 20.4142 33.2055 23.8498 33.2055Z" fill="#83A3EB" />
                        </svg>
                    </a>
                    <a href='https://www.youtube.com/@megamix_official' className="footer__media-social-link">
                        <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M34.515 7.30079C28.5408 6.89892 15.2347 6.90055 9.26883 7.30079C2.80894 7.73533 2.04807 11.5808 2 21.7027C2.04807 31.8066 2.80231 35.6684 9.26883 36.1046C15.2364 36.5049 28.5408 36.5065 34.515 36.1046C40.9748 35.6701 41.7357 31.8246 41.7838 21.7027C41.7357 11.5988 40.9815 7.73696 34.515 7.30079ZM16.9189 28.2371V15.1683L30.1802 21.6913L16.9189 28.2371Z" fill="#83A3EB" />
                        </svg>
                    </a>
                    <a href='https://t.me/gk_megamix' className="footer__media-social-link">
                        <svg width="39" height="39" viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_3877_1430)">
                                <path fillRule="evenodd" clipRule="evenodd" d="M29.15 36.1181C29.6606 36.4796 30.3186 36.57 30.9053 36.348C31.4919 36.1245 31.9232 35.6234 32.0532 35.0161C33.4311 28.5406 36.7735 12.1504 38.0277 6.25996C38.1229 5.816 37.9643 5.35459 37.6155 5.05809C37.2666 4.76158 36.783 4.67596 36.3518 4.83611C29.7034 7.29694 9.22874 14.9791 0.860018 18.0757C0.328847 18.2724 -0.0168106 18.7829 0.000630831 19.3426C0.0196579 19.9039 0.397027 20.3907 0.940883 20.554C4.69396 21.6766 9.62038 23.2384 9.62038 23.2384C9.62038 23.2384 11.9226 30.1912 13.1229 33.7271C13.2736 34.171 13.6208 34.5199 14.079 34.6404C14.5357 34.7593 15.0241 34.634 15.365 34.3121C17.293 32.4919 20.2739 29.6775 20.2739 29.6775C20.2739 29.6775 25.9376 33.8301 29.15 36.1181ZM11.6927 22.36L14.3549 31.141L14.9464 25.5803C14.9464 25.5803 25.2321 16.3031 31.0955 11.0151C31.2668 10.8597 31.2906 10.5997 31.1479 10.4174C31.0068 10.235 30.7467 10.1922 30.5517 10.3159C23.7559 14.6556 11.6927 22.36 11.6927 22.36Z" fill="#83A3EB" />
                            </g>
                            <defs>
                                <clipPath id="clip0_3877_1430">
                                    <rect width="38.0541" height="38.0541" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </a>
                </div>
            </div>
            {isSuccess ?
                <div className="for-partners__success-block">
                    <p className="for-partners__success-text">
                        Спасибо! <br />
                        Ваше сообщение отправлено, мы <br className='for-partners__success-text_translate' /> постараемся ответить как можно скорее.
                    </p>
                    <button
                        onClick={() => setIsSuccess(false)}
                        className={`for-partners__form-button for-partners__form-button_active`}
                    >
                        Новое сообщение
                    </button>
                </div>
                :
                <form
                    action='/submit'
                    method='post'
                    className='for-partners__form'
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <div className='for-partners__form-fields'>
                        <div className='for-partners__form-person'>
                            <ForPartnersNameInput
                                errors={errors}
                                register={register}
                            />
                            <ForPartnersMail
                                errors={errors}
                                register={register}
                            />
                            <ForPartnersPhone
                                value={phoneValue}
                                setValue={(val: string) => setPhoneValue(val)}
                                errors={errors}
                                register={register}
                            />
                        </div>
                        <div className='for-partners__form-message'>
                            {regions && (
                                <ForPartnersAutocomplete
                                    {...register('region', { required: false })}
                                    isOpen={statesOfSelects.region.isOpen}
                                    selectedOption={statesOfSelects.region.selected}
                                    stateKey={'region'}
                                    setState={(key, value) => updateStateOfSelect(key, value)}
                                    toggleState={key => toggleStateOfSelect(key)}
                                    setOption={(key, value) => {
                                        setOption(key, value)
                                        setValue('region', value)
                                    }}
                                    list={regions}
                                />
                            )}
                            <ForPartnersSelect
                                {...register('theme', { required: true })}
                                labelText={labelsList.topic}
                                isOpen={statesOfSelects.theme.isOpen}
                                selectedOption={statesOfSelects.theme.selected}
                                stateKey={'theme'}
                                setState={(key, value) => updateStateOfSelect(key, value)}
                                toggleState={key => toggleStateOfSelect(key)}
                                setOption={(key, value) => {
                                    setOption(key, value)
                                    setValue('theme', value === selectTheme[0].name ? '' : value)
                                }}
                                list={selectTheme}
                            />
                            {statesOfSelects.theme.selected === 'приобрести продукцию' ||
                                statesOfSelects.theme.selected === 'получить консультацию' ? (
                                <ForPartnersSelect
                                    {...register('direction', { required: false })}
                                    labelText={labelsList.direction}
                                    isOpen={statesOfSelects.direction.isOpen}
                                    selectedOption={statesOfSelects.direction.selected}
                                    stateKey={'direction'}
                                    setState={(key, value) => updateStateOfSelect(key, value)}
                                    toggleState={key => toggleStateOfSelect(key)}
                                    setOption={(key, value) => {
                                        setOption(key, value)
                                        setValue('direction', value === selectDirection[0].name ? '' : value)
                                    }}
                                    list={selectDirection}
                                />
                            ) : null}
                            {statesOfSelects.theme.selected === 'приобрести продукцию' &&
                                <>
                                    <ForPartnersSelect
                                        {...register('organization-type', { required: false })}
                                        labelText={labelsList.organizationType}
                                        isOpen={statesOfSelects.organizationType.isOpen}
                                        selectedOption={statesOfSelects.organizationType.selected}
                                        stateKey={'organizationType'}
                                        setState={(key, value) => updateStateOfSelect(key, value)}
                                        toggleState={key => toggleStateOfSelect(key)}
                                        setOption={(key, value) => {
                                            setOption(key, value)
                                            setValue('organization-type', value === selectOrganization[0].name ? '' : value)
                                        }}
                                        list={selectOrganization}
                                    />
                                    <ForPartnersOrganization
                                        register={register}
                                        errors={errors}
                                    />
                                    <ForPartnersSelect
                                        {...register('heads-amount', { required: false })}
                                        labelText={labelsList.amountOfHeads}
                                        isOpen={statesOfSelects.amountOfHeads.isOpen}
                                        selectedOption={statesOfSelects.amountOfHeads.selected}
                                        stateKey={'amountOfHeads'}
                                        setState={(key, value) => updateStateOfSelect(key, value)}
                                        toggleState={key => toggleStateOfSelect(key)}
                                        setOption={(key, value) => {
                                            setOption(key, value)
                                            setValue('heads-amount', value === selectAmountOfHeads[0].name ? '' : value)
                                        }}
                                        list={selectAmountOfHeads}
                                    />
                                </>

                            }
                            {/* {!statesOfSelects.region.isOpen &&
                            !statesOfSelects.theme.isOpen && */}
                            <ForPartnersMessage
                                messageValue={messageValue}
                                setValue={(val) => setMessageValue(val)}
                                register={register}

                            // setValue={(val) => setValue('message', val)}
                            />
                            {/* // } */}
                            {/* {!statesOfSelects.region.isOpen &&
                            !statesOfSelects.theme.isOpen && */}
                            <ForPartnersAgree setValue={(state: boolean) => setValue('robot', state)} register={register} />
                            {/* // } */}
                        </div>
                    </div>
                    <button
                        disabled={!fieldsFilled}
                        className={`for-partners__form-button ${fieldsFilled && 'for-partners__form-button_active'}`}
                    >
                        {t("send")}
                    </button>
                </form>}
        </div>
    )
}

export default ForPartners
